import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone } from "@phosphor-icons/react";
import { CONTACT } from "../lib/api";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/calculator", label: "Calculator" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-[#E5E3DB]" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="container-custom flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-10 h-10 rounded-xl bg-[#135934] flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
              <path d="M12 2L13.5 6.5H18L14.25 9.5L15.75 14L12 11L8.25 14L9.75 9.5L6 6.5H10.5L12 2Z"/>
              <path d="M12 17C14.76 17 17 19.24 17 22H7C7 19.24 9.24 17 12 17Z"/>
            </svg>
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-lg tracking-tight text-[#0A1F15]">Orax Solar</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#5C7368] font-semibold">Akola · MH</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition ${
                  isActive ? "bg-[#0A1F15] text-white" : "text-[#2D4739] hover:bg-[#F0EFEB]"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${CONTACT.phonePrimary}`}
            className="hidden md:inline-flex btn-blue text-sm !py-2.5"
            data-testid="nav-call-button"
          >
            <Phone weight="bold" size={16} /> {CONTACT.phonePrimary}
          </a>
          <button
            className="lg:hidden p-2 rounded-full hover:bg-[#F0EFEB]"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-[#E5E3DB] px-6 py-4">
          <div className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-xl text-base font-medium ${
                    isActive ? "bg-[#0A1F15] text-white" : "text-[#2D4739] hover:bg-[#F0EFEB]"
                  }`
                }
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={`tel:${CONTACT.phonePrimary}`}
              className="mt-3 btn-blue w-full"
              data-testid="mobile-call-button"
            >
              <Phone weight="bold" size={16} /> Call {CONTACT.phonePrimary}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
